import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Mail, Image as ImageIcon, Save, Send, Eye, RotateCcw, Loader2,
  ChevronRight, Tag, X, Upload, CheckCircle2, Edit3,
} from 'lucide-react';


function SectionCard({ title, description, action, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#1A1A1A]">{title}</h3>
          {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}


/* ── Branding panel (logo + accent + footer) ───────────────────────────────── */
function BrandingPanel({ branding, setBranding, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadLogo = async (file) => {
    if (!file) return;
    const MAX = 2 * 1024 * 1024;
    if (file.size > MAX) {
      toast.error('File troppo grande (max 2 MB)');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('logo', file);
    try {
      const r = await adminApi.post('/superadmin/lazo-emails/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBranding({ ...branding, logo_url: r.data.logo_url });
      toast.success('Logo caricato');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    }
    setUploading(false);
  };

  const removeLogo = async () => {
    setSaving(true);
    try {
      await adminApi.put('/superadmin/lazo-emails/branding', { ...branding, logo_url: '' });
      setBranding({ ...branding, logo_url: '' });
      toast.success('Logo rimosso');
    } catch (e) {
      toast.error('Errore');
    }
    setSaving(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put('/superadmin/lazo-emails/branding', branding);
      toast.success('Salvato');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
    setSaving(false);
  };

  return (
    <SectionCard
      title="Brandizzazione email"
      description="Logo, colore d'accento e footer applicati a tutte le email transazionali inviate da Lazo."
      action={
        <button onClick={save} disabled={saving} data-testid="save-email-branding"
          className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:shadow-md disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Salva</>}
        </button>
      }>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Logo */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-2">Logo Lazo</label>
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center bg-slate-50" data-testid="logo-upload-area">
            {branding.logo_url ? (
              <div>
                <img src={branding.logo_url} alt="Logo Lazo" className="max-h-14 mx-auto mb-2 object-contain" data-testid="current-logo" />
                <button onClick={removeLogo} className="text-[11px] text-red-500 font-semibold hover:underline">
                  Rimuovi
                </button>
              </div>
            ) : (
              <div>
                <ImageIcon size={24} className="mx-auto mb-1.5 text-slate-300" />
                <p className="text-[11px] text-slate-500">Nessun logo caricato</p>
              </div>
            )}
          </div>
          <label className="mt-2 flex items-center justify-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold cursor-pointer hover:bg-slate-50">
            <Upload size={12} />
            {uploading ? <Loader2 size={12} className="animate-spin" /> : 'Carica logo'}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                   onChange={(e) => uploadLogo(e.target.files?.[0])}
                   data-testid="logo-file-input" />
          </label>
          <p className="text-[10px] text-slate-400 mt-1">PNG, JPG, WebP o GIF — max 2 MB. Altezza ideale 48px.</p>
        </div>

        {/* Accent color */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-2">Colore d'accento</label>
          <div className="flex items-center gap-2">
            <input type="color" value={branding.accent_color || '#D44E20'}
              onChange={e => setBranding({ ...branding, accent_color: e.target.value })}
              data-testid="accent-color-input"
              className="w-12 h-10 border border-slate-200 rounded-lg cursor-pointer" />
            <input type="text" value={branding.accent_color || ''}
              onChange={e => setBranding({ ...branding, accent_color: e.target.value })}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#D44E20]" />
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Usato per titoli e pulsante CTA.</p>
        </div>

        {/* Footer */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-2">Testo footer</label>
          <input type="text" value={branding.footer_text || ''}
            onChange={e => setBranding({ ...branding, footer_text: e.target.value })}
            data-testid="footer-text-input"
            placeholder="Lazo SaaS — Multi-Brand Loyalty"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
          <p className="text-[10px] text-slate-400 mt-1">Mostrato sotto ogni email.</p>
        </div>
      </div>
    </SectionCard>
  );
}


/* ── Template editor modal (subject + plain message + cta label) ──────────── */
function TemplateEditor({ templateKey, onClose, onSaved }) {
  const [tpl, setTpl] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/superadmin/lazo-emails/templates/${templateKey}`);
      setTpl(r.data);
    } catch (e) {
      toast.error('Errore caricamento');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [templateKey]);

  const regeneratePreview = async () => {
    try {
      // Save current draft first? No — preview uses DB state. So we need to pass overrides.
      // Simpler: save draft, preview, user can reset if dislikes.
      // For now we do save-then-preview:
      await adminApi.put(`/superadmin/lazo-emails/templates/${templateKey}`, {
        subject: tpl.subject, message: tpl.message, cta_label: tpl.cta_label,
      });
      const r = await adminApi.post(`/superadmin/lazo-emails/templates/${templateKey}/preview`, {});
      setPreviewHtml(r.data.html);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore anteprima');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/superadmin/lazo-emails/templates/${templateKey}`, {
        subject: tpl.subject, message: tpl.message, cta_label: tpl.cta_label,
      });
      toast.success('Template salvato');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm('Ripristinare il testo originale? Le tue modifiche andranno perse.')) return;
    try {
      await adminApi.delete(`/superadmin/lazo-emails/templates/${templateKey}`);
      toast.success('Ripristinato');
      load();
      onSaved && onSaved();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Inserisci una email valida');
      return;
    }
    setSendingTest(true);
    try {
      // Save first to include latest draft
      await adminApi.put(`/superadmin/lazo-emails/templates/${templateKey}`, {
        subject: tpl.subject, message: tpl.message, cta_label: tpl.cta_label,
      });
      const r = await adminApi.post(`/superadmin/lazo-emails/templates/${templateKey}/test`, { to_email: testEmail });
      if (r.data.sent) {
        toast.success(`Email inviata a ${testEmail}`);
      } else {
        toast.warning('Email in modalità mock — configura RESEND_API_KEY per invio reale');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio');
    }
    setSendingTest(false);
  };

  const insertVariable = (v) => {
    setTpl({ ...tpl, message: `${tpl.message || ''}{{${v}}}` });
  };

  if (loading || !tpl) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Loader2 size={28} className="animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="template-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2">
              <Edit3 size={16} className="text-[#D44E20]" />
              <h3 className="text-lg font-bold text-[#1A1A1A]">{tpl.label}</h3>
              {tpl.is_custom && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#D44E20]/10 text-[#D44E20]">Personalizzato</span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">{tpl.description}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-[#1A1A1A]"><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Editor */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Oggetto</label>
            <input type="text" value={tpl.subject}
              onChange={e => setTpl({ ...tpl, subject: e.target.value })}
              data-testid="template-subject"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20] mb-4" />

            <label className="block text-xs font-semibold text-slate-600 mb-1">Messaggio</label>
            <textarea value={tpl.message}
              onChange={e => setTpl({ ...tpl, message: e.target.value })}
              rows={10}
              data-testid="template-message"
              placeholder="Ciao {{brand_name}},&#10;&#10;Testo del messaggio..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#D44E20] mb-2" />
            <p className="text-[10px] text-slate-400 mb-3">
              Usa una riga vuota per separare i paragrafi. Supporta &lt;b&gt;grassetto&lt;/b&gt;.
            </p>

            <label className="block text-xs font-semibold text-slate-600 mb-1">Testo pulsante (CTA, opzionale)</label>
            <input type="text" value={tpl.cta_label || ''}
              onChange={e => setTpl({ ...tpl, cta_label: e.target.value })}
              data-testid="template-cta-label"
              placeholder="Es. Vai al pannello Lazo"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20] mb-4" />

            {/* Available placeholders */}
            {tpl.variables && tpl.variables.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                  <Tag size={11} /> Placeholder disponibili
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {tpl.variables.map(v => (
                    <button key={v} onClick={() => insertVariable(v)}
                      data-testid={`insert-var-${v}`}
                      className="text-[11px] font-mono px-2 py-1 rounded-md bg-slate-100 hover:bg-[#D44E20]/10 hover:text-[#D44E20] text-slate-600 transition-colors">
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-2">Clicca per inserirli nel messaggio.</p>
              </div>
            )}
          </div>

          {/* Preview + test */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Anteprima</label>
              <button onClick={regeneratePreview} data-testid="refresh-preview"
                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 font-semibold">
                <Eye size={11} /> Aggiorna
              </button>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50" style={{ height: 340 }}>
              {previewHtml ? (
                <iframe
                  title="Anteprima email"
                  srcDoc={previewHtml}
                  className="w-full h-full border-0"
                  data-testid="preview-iframe"
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                  <Eye size={24} className="opacity-40" />
                  Clicca "Aggiorna" per vedere l'anteprima
                </div>
              )}
            </div>

            {/* Test send */}
            <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-slate-50">
              <p className="text-xs font-semibold text-slate-600 mb-2">Invia email di test</p>
              <div className="flex items-center gap-2">
                <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                  placeholder="tua@email.it"
                  data-testid="test-email-input"
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
                <button onClick={sendTest} disabled={sendingTest} data-testid="send-test-email"
                  className="px-3 py-2 bg-[#D44E20] text-white text-xs font-semibold rounded-lg hover:shadow-md disabled:opacity-50 flex items-center gap-1.5">
                  {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <><Send size={12} /> Invia test</>}
                </button>
              </div>
              <p className="text-[10px] text-slate-500 mt-2">
                Richiede RESEND_API_KEY configurata. Con dati di esempio.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={reset} data-testid="reset-template"
            className="flex items-center gap-2 text-xs text-slate-500 hover:text-red-500 font-semibold">
            <RotateCcw size={12} /> Ripristina default
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-100">
              Chiudi
            </button>
            <button onClick={save} disabled={saving} data-testid="save-template"
              className="px-5 py-2 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Salva modifiche</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ── Main page ─────────────────────────────────────────────────────────────── */
export default function SuperadminLazoEmails() {
  const [templates, setTemplates] = useState([]);
  const [branding, setBranding] = useState({ logo_url: '', accent_color: '#D44E20', footer_text: '' });
  const [loading, setLoading] = useState(true);
  const [editKey, setEditKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        adminApi.get('/superadmin/lazo-emails/templates'),
        adminApi.get('/superadmin/lazo-emails/branding'),
      ]);
      setTemplates(r1.data.templates || []);
      setBranding(r2.data || {});
    } catch (e) {
      toast.error('Errore caricamento');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="superadmin-lazo-emails-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={22} className="text-[#D44E20]" />
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">Email transazionali Lazo</h1>
        </div>
        <p className="text-sm text-slate-500">
          Personalizza oggetto, messaggio e call-to-action di ogni email che Lazo invia ai brand clienti.
          Il logo e il colore d'accento sono applicati a tutte.
        </p>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 flex justify-center">
          <Loader2 size={28} className="animate-spin text-[#D44E20]" />
        </div>
      ) : (
        <>
          <BrandingPanel branding={branding} setBranding={setBranding} onSaved={load} />

          <SectionCard
            title={`Template email (${templates.length})`}
            description="Clicca su un template per modificarne oggetto, messaggio e call-to-action.">
            <div className="divide-y divide-slate-100" data-testid="templates-list">
              {templates.map(t => (
                <button key={t.key} onClick={() => setEditKey(t.key)}
                  data-testid={`template-row-${t.key}`}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-[#1A1A1A]">{t.label}</span>
                      {t.is_custom ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#D44E20]/10 text-[#D44E20]">
                          Personalizzato
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{t.subject}</p>
                  </div>
                  <ChevronRight size={16} className="text-slate-400 flex-shrink-0 ml-3" />
                </button>
              ))}
            </div>
          </SectionCard>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
            <div className="flex items-start gap-2">
              <CheckCircle2 size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold mb-1">Come funziona?</p>
                <p className="text-xs leading-relaxed">
                  Le modifiche vengono applicate immediatamente a tutte le email future.
                  Se il servizio email (Resend) non è configurato, le email vengono comunque registrate nei log
                  e i reminder tracciati normalmente, ma non inviate realmente.
                  Puoi sempre ripristinare il testo di default di un template.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {editKey && (
        <TemplateEditor
          templateKey={editKey}
          onClose={() => setEditKey(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
